import * as THREE from 'three';
import { SurfaceType } from '../terrain/SurfaceType';
import type { BallState } from './BallState';
import type { CollisionConfig } from './CollisionResolver';
import { DEFAULT_GREEN_SPEED_PROFILE, type GreenSpeedProfile } from './GreenSpeedProfile';
import type { SurfaceSample } from './TerrainSurfaceProbe';

export class PuttRollingModel {
  private belowStopSeconds = 0;
  constructor(
    private readonly collisionConfig: CollisionConfig,
    private readonly greenSpeed: GreenSpeedProfile = DEFAULT_GREEN_SPEED_PROFILE,
  ) {}

  apply(state: BallState, sample: SurfaceSample, deltaSeconds: number): void {
    if (sample.properties.type !== SurfaceType.Green) {
      this.belowStopSeconds = 0;
      state.motionMode = 'genericRoll';
      return;
    }
    state.surfaceType = SurfaceType.Green;
    const normal = sample.normal;
    const tangentVelocity = state.velocity.clone().projectOnPlane(normal);
    const tangentSpeed = tangentVelocity.length();
    const downhill = new THREE.Vector3(0, -9.81, 0).projectOnPlane(normal);
    const resistance =
      tangentSpeed > 0.0001
        ? tangentVelocity
            .clone()
            .normalize()
            .multiplyScalar(-this.greenSpeed.kineticDecelerationMetersPerSecondSquared)
        : new THREE.Vector3();
    const acceleration = downhill.add(resistance);
    const nextVelocity = tangentVelocity.addScaledVector(acceleration, deltaSeconds);

    if (
      tangentSpeed > 0 &&
      nextVelocity.dot(tangentVelocity) < 0 &&
      downhill.length() < this.greenSpeed.staticHoldAcceleration
    ) {
      nextVelocity.set(0, 0, 0);
    }

    state.velocity.copy(nextVelocity.projectOnPlane(normal));
    state.spin.multiplyScalar(Math.max(0, 1 - 0.9 * deltaSeconds));

    const speed = state.velocity.length();
    if (
      speed < this.greenSpeed.stopSpeedMetersPerSecond &&
      downhill.length() < this.greenSpeed.staticHoldAcceleration
    ) {
      this.belowStopSeconds += deltaSeconds;
    } else {
      this.belowStopSeconds = 0;
    }
    if (this.belowStopSeconds >= 0.18 || speed < this.collisionConfig.restSpeedThreshold * 0.35) {
      state.velocity.set(0, 0, 0);
      state.spin.set(0, 0, 0);
      state.isResting = true;
      state.phase = 'resting';
      state.motionMode = 'resting';
      this.belowStopSeconds = 0;
    }
  }
}
